import { FC } from 'react'

import styled from 'styled-components'
import { Skeleton } from 'antd'
import { SkeletonNodeProps } from 'antd/es/skeleton/Node'

import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
  CampaignTypeEnum,
} from 'src/Types/campaigns'
import { TrackInfo } from 'src/Components/TrackInfo'
import { CampaignPayment } from 'src/Redux/artistOrderApi/campaignPayment'
import { TaskForCreators } from 'src/Components/TaskForCreators'
import { LinkButton } from 'src/Components/Buttons/LinkButton'

import { BudgetSection } from '../BudgetSection'

const SummaryWrapper = styled.div`
  display: flex;
  width: 320px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  position: sticky;
  top: 0px;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    position: relative;
    width: 100%;
    gap: 48px;
  }
`

const StyledSkeleton = styled(({ className, ...rest }: SkeletonNodeProps) => (
  <Skeleton.Input {...rest} rootClassName={className} active />
))`
  &.ant-skeleton-block {
    border-radius: 16px;
  }
  &.ant-skeleton {
    flex-grow: 1 !important;
    @media (max-width: ${MAX_MOBILE_WIDTH}) {
      width: 100% !important;
    }
  }
  & > .ant-skeleton-input {
    width: 100% !important;
    height: 240px !important;
    border-radius: 16px !important;
    background: ${({ theme }) => theme.color.lightGrey} !important;
  }
`

interface SummarySectionProps {
  loading: boolean
  campaignStatus?: CampaignStatusEnum
  payment?: CampaignPayment | null
  budget?: string
  spent?: number
  trackCoverUrl?: string
  trackName?: string
  trackAuthorName?: string
  startDate?: string
  endDate?: string
  genres?: Array<string>
  reserved?: number
  hasNotCompletedEvents?: boolean
  campaignType?: CampaignTypeEnum
  credits?: number
  platform: CampaignPlatformTypeEnum
  commentType?: string | null
  lyricsLink?: string | null
}

export const SummarySection: FC<SummarySectionProps> = ({
  campaignStatus,
  payment,
  budget = 0.0,
  spent = 0.0,
  loading,
  trackCoverUrl,
  trackName,
  trackAuthorName,
  startDate,
  endDate,
  reserved,
  hasNotCompletedEvents,
  credits,
  platform,
  commentType,
  lyricsLink,
}) => {
  if (loading) {
    return <StyledSkeleton />
  }

  if (!campaignStatus) {
    return null
  }

  const taskSectionVisible = ![
    CampaignStatusEnum.AWAITING_REVIEW,
    CampaignStatusEnum.PENDING,
  ].includes(campaignStatus)

  return (
    <SummaryWrapper>
      <TrackInfo
        platform={platform}
        status={campaignStatus}
        paymentState={payment?.state}
        trackImage={trackCoverUrl}
        trackName={trackName}
        artistName={trackAuthorName}
        startDate={startDate}
        endDate={endDate}
        taskForCreators={
          taskSectionVisible ? (
            <TaskForCreators commentType={commentType} />
          ) : null
        }
      >
        {lyricsLink ? (
          <LinkButton href={lyricsLink}>Link to lyrics</LinkButton>
        ) : null}
      </TrackInfo>
      <BudgetSection
        campaignStatus={campaignStatus}
        budget={Number(budget)}
        spent={spent || 0}
        reserved={reserved}
        hasNotCompletedEvents={hasNotCompletedEvents}
        credits={credits}
      />
    </SummaryWrapper>
  )
}
